import {
  signatureProofSchema,
  type ChangePasswordInput,
  type ExpiredPasswordChangeInput,
  type LoginInput,
  type RequestSignatureProofInput,
  type SignatureProof,
} from '@qualicloud/contracts';
import { ApiScope, request, setAccessToken } from './client';
import type { LoginResponse, MfaEnrollmentResponse, ProfileResponse } from './types';

/**
 * Autenticação e assinatura — serviço IDENTITY.
 *
 * O access token devolvido pelo login é guardado APENAS em memória
 * (`setAccessToken`). O refresh token vem no corpo por compatibilidade com o
 * contrato do serviço, mas quem o persiste é o GATEWAY, como cookie httpOnly:
 * o cliente descarta o valor imediatamente e nunca o grava.
 */
export const authApi = {
  async login(input: LoginInput): Promise<LoginResponse> {
    const response = await request<LoginResponse>({
      scope: ApiScope.IDENTITY,
      path: '/auth/login',
      method: 'POST',
      body: input,
      // Não faz sentido tentar renovar sessão durante o login.
      skipAuthRefresh: true,
    });

    setAccessToken(response.accessToken, response.accessTokenExpiresAt);
    return response;
  },

  async logout(): Promise<void> {
    await request({
      scope: ApiScope.IDENTITY,
      path: '/auth/logout',
      method: 'POST',
      // O gateway lê o refresh do cookie httpOnly e revoga a família inteira.
      body: {},
    });
  },

  profile(): Promise<ProfileResponse> {
    return request<ProfileResponse>({ scope: ApiScope.IDENTITY, path: '/auth/me' });
  },

  async changePassword(input: ChangePasswordInput): Promise<void> {
    await request({
      scope: ApiScope.IDENTITY,
      path: '/auth/password',
      method: 'POST',
      body: input,
    });
  },

  /**
   * Troca de senha VENCIDA, sem sessão.
   *
   * O login com senha vencida recusa com `AUTH.PASSWORD_EXPIRED` e não emite
   * token nenhum — é o correto por §11.300. Como `/auth/password` é rota
   * autenticada, sem esta aqui o usuário ficava preso na tela de troca. A
   * credencial atual, vencida, é o que autentica o ato; nenhuma sessão é
   * aberta, e o usuário entra de novo com a senha nova.
   */
  async changeExpiredPassword(input: ExpiredPasswordChangeInput): Promise<void> {
    await request({
      scope: ApiScope.IDENTITY,
      path: '/auth/password/expired',
      method: 'POST',
      body: input,
      // Não há sessão para renovar: um 401 aqui é credencial errada, não
      // sessão vencida. Tentar renovar transformaria "senha incorreta" em
      // "sua sessão expirou".
      skipAuthRefresh: true,
    });
  },

  enrollMfa(): Promise<MfaEnrollmentResponse> {
    return request<MfaEnrollmentResponse>({
      scope: ApiScope.IDENTITY,
      path: '/auth/mfa/enroll',
      method: 'POST',
      body: {},
    });
  },

  async activateMfa(totp: string): Promise<void> {
    await request({
      scope: ApiScope.IDENTITY,
      path: '/auth/mfa/activate',
      method: 'POST',
      body: { totp },
    });
  },

  async disableMfa(password: string): Promise<void> {
    await request({
      scope: ApiScope.IDENTITY,
      path: '/auth/mfa',
      method: 'DELETE',
      body: { password },
    });
  },
};

export const signatureApi = {
  /**
   * Passo 3 do fluxo do plano §5.1: o IDENTITY valida usuário + senha (+ MFA),
   * confere que o solicitante é o titular da sessão e que tem autoridade para
   * o ato, e devolve uma prova de USO ÚNICO com TTL de 60 s.
   *
   * A resposta é parseada por `signatureProofSchema`, o mesmo schema que o
   * serviço usa — `expiresAt` chega como `Date`, que é o que a contagem
   * regressiva do diálogo precisa.
   */
  requestProof(input: RequestSignatureProofInput): Promise<SignatureProof> {
    return request<SignatureProof>({
      scope: ApiScope.IDENTITY,
      path: '/signatures/proof',
      method: 'POST',
      body: input,
      responseSchema: signatureProofSchema,
    });
  },
};
